import type {ISketchObject} from './ISketchObject';
import type {SketchRelationDefinition} from '../../sketch/solver/constraint/relationDefinition/SketchRelationDefinition';
import {isSingleTargetRelation} from '../../sketch/solver/constraint/relationDefinition/SingleTargetRelation';
import {isMultiTargetRelation} from '../../sketch/solver/constraint/relationDefinition/MultiTargetRelation';
import {createSketchConstraintPersistentId} from '../../sketch/identity/SketchPersistentIdFactory';


/**
 * 复制草图对象上的单目标几何关系。
 * 说明：
 * 1. 仅复制 `SingleTargetRelation`；
 * 2. 多目标约束（同一约束实例绑定多个对象）不会复制；
 * 3. 复制时创建新的约束实例并绑定到目标对象，避免与源对象共享约束实例。
 * @param source 源对象。
 * @param target 目标对象。
 * @param shouldCloneConstraint 可选约束复制准入策略。
 */
export function cloneSingleTargetConstraints(
    source: ISketchObject,
    target: ISketchObject,
    shouldCloneConstraint?: (constraint: SketchRelationDefinition<ISketchObject>) => boolean,
): void {
    const sourceConstraints = source.constraints.getRelations();
    sourceConstraints.forEach((sourceConstraint) => {
        // 引用悬空关系及其诊断占位均由仓库状态派生，不得作为草图定义复制到新实体。
        if (sourceConstraint.isTransientDiagnosticConstraint || sourceConstraint.isDangling) {
            return;
        }
        if (!isSingleTargetRelation(sourceConstraint)) {
            return;
        }
        if (shouldCloneConstraint && !shouldCloneConstraint(sourceConstraint as SketchRelationDefinition<ISketchObject>)) {
            return;
        }

        const clonedConstraint = cloneConstraintInstance(sourceConstraint);
        target.constraints.addRelation(
            clonedConstraint as Parameters<typeof target.constraints.addRelation>[0],
        );
    });
}

/**
 * 复制多目标几何关系。
 * 说明：
 * 1. 输入对象列表应来自同一复制域（如同一草图组）；
 * 2. 仅当所有目标都能在 `objectMap` 中找到副本时才会复制；
 * 3. 每条多目标关系仅复制一次，并绑定到所有对应副本对象。
 * @param sourceObjects 源对象列表。
 * @param objectMap 源 UUID 到副本对象映射。
 * @param shouldCloneConstraint 可选约束复制准入策略。
 */
export function cloneMultiTargetConstraints(
    sourceObjects: readonly ISketchObject[],
    objectMap: ReadonlyMap<string, ISketchObject>,
    shouldCloneConstraint?: (constraint: SketchRelationDefinition<ISketchObject>) => boolean,
): void {
    const visitedConstraintSet = new Set<SketchRelationDefinition<ISketchObject>>();
    sourceObjects.forEach((sourceObject) => {
        const sourceConstraints = sourceObject.constraints.getRelations();
        sourceConstraints.forEach((sourceConstraint) => {
            const normalizedConstraint = sourceConstraint as SketchRelationDefinition<ISketchObject>;
            if (visitedConstraintSet.has(normalizedConstraint)) {
                return;
            }
            visitedConstraintSet.add(normalizedConstraint);

            if (!isMultiTargetRelation(normalizedConstraint)) {
                return;
            }
            if (shouldCloneConstraint && !shouldCloneConstraint(normalizedConstraint)) {
                return;
            }

            const sourceTargets = normalizedConstraint.getTargetsSnapshot();
            if (sourceTargets.length <= 1) {
                return;
            }

            const clonedTargets = sourceTargets.map((sourceTarget) => {
                const clonedTarget = objectMap.get(sourceTarget.uuid);
                if (!clonedTarget) {
                    throw new Error(`多目标关系复制失败：未找到目标对象副本（uuid=${sourceTarget.uuid}）。`);
                }
                return clonedTarget;
            });

            const clonedConstraint = cloneConstraintInstance(normalizedConstraint);
            clonedTargets.forEach((clonedTarget) => {
                clonedTarget.constraints.addRelation(
                    clonedConstraint as Parameters<typeof clonedTarget.constraints.addRelation>[0],
                );
            });
        });
    });
}

/**
 * 复制当前复制域内部完整闭合的多目标几何关系。
 * 说明：
 * 1. 复制实体只会复制用户选中的一部分草图对象，不像复制草图那样天然拥有整组对象映射；
 * 2. 因此遇到目标包含未复制对象的多目标关系时必须跳过，避免把副本重新约束到源对象或外部对象；
 * 3. 所有目标都能在 `objectMap` 中找到副本时，才创建新的约束实例并绑定到副本对象。
 * @param sourceObjects 源对象列表。
 * @param objectMap 源 UUID 到副本对象映射。
 * @param shouldCloneConstraint 可选约束复制准入策略。
 */
export function cloneContainedMultiTargetConstraints(
    sourceObjects: readonly ISketchObject[],
    objectMap: ReadonlyMap<string, ISketchObject>,
    shouldCloneConstraint?: (constraint: SketchRelationDefinition<ISketchObject>) => boolean,
): void {
    const visitedConstraintSet = new Set<SketchRelationDefinition<ISketchObject>>();
    sourceObjects.forEach((sourceObject) => {
        const sourceConstraints = sourceObject.constraints.getRelations();
        sourceConstraints.forEach((sourceConstraint) => {
            const normalizedConstraint = sourceConstraint as SketchRelationDefinition<ISketchObject>;
            if (visitedConstraintSet.has(normalizedConstraint)) {
                return;
            }
            visitedConstraintSet.add(normalizedConstraint);

            if (!isMultiTargetRelation(normalizedConstraint)) {
                return;
            }
            if (shouldCloneConstraint && !shouldCloneConstraint(normalizedConstraint)) {
                return;
            }

            const sourceTargets = normalizedConstraint.getTargetsSnapshot();
            if (sourceTargets.length <= 1) {
                return;
            }

            const clonedTargets = sourceTargets
                .map((sourceTarget) => objectMap.get(sourceTarget.uuid) ?? null);

            // 复制实体只复制当前选择域内部关系，跨选择域关系留给后续“保留几何关系”能力处理。
            if (clonedTargets.some((target) => target === null)) {
                return;
            }

            const clonedConstraint = cloneConstraintInstance(normalizedConstraint);
            /**
             * 复制实体产出的是“新逻辑关系”，与源关系并存于同一草图，
             * 必须重新生成持久 ID，避免同组内出现重复关系身份。
             */
            clonedConstraint.persistentId = createSketchConstraintPersistentId();
            clonedTargets.forEach((clonedTarget) => {
                clonedTarget?.constraints.addRelation(
                    clonedConstraint as Parameters<typeof clonedTarget.constraints.addRelation>[0],
                );
            });
        });
    });
}

/**
 * 基于源约束构造新的同类约束实例。
 * 说明：
 * 1. 优先调用约束自身的 `cloneSelf()` 方法，以支持带参数构造的约束（如 ReferenceDriven* 系列）；
 * 2. `cloneSelf()` 返回 null 时，回退到无参构造路径，适用于无状态或无参数的普通约束；
 * 3. 克隆完成后统一同步持久身份与尺寸归属，不复制任何运行时求解阶段状态。
 * @param sourceConstraint 源约束。
 * @returns 新建约束实例。
 * @throws 当无法从源约束解析可调用的构造器时抛出错误。
 * @throws 当源约束未提供可用的 `cloneSelf()`，且无参构造失败时抛出包含原始错误信息的新错误。
 */
export function cloneConstraintInstance<TTarget extends ISketchObject>(
    sourceConstraint: SketchRelationDefinition<TTarget>,
): SketchRelationDefinition<TTarget> {
    // 优先走带参克隆路径：约束实现了 cloneSelf() 说明它需要把内部状态带入克隆实例。
    const selfCloned = sourceConstraint.cloneSelf();
    if (selfCloned !== null) {
        // 克隆默认表达同一逻辑关系（历史快照/恢复语义），必须继承持久身份。
        selfCloned.persistentId = sourceConstraint.persistentId;
        selfCloned.setSuppressed(sourceConstraint.isSuppressed);
        selfCloned.setBranchHint(sourceConstraint.getBranchHint());
        return selfCloned;
    }

    // 回退路径：无参构造（适用于 PointFixConstraint、HorizontalConstraint 等无状态约束）。
    const constraintConstructor = sourceConstraint.constructor as unknown;
    if (typeof constraintConstructor !== 'function') {
        throw new Error('约束复制失败：无法解析约束构造器。');
    }

    try {
        const ConstraintClass = constraintConstructor as new () => SketchRelationDefinition<TTarget>;
        const clonedConstraint = new ConstraintClass();
        // 克隆默认表达同一逻辑关系（历史快照/恢复语义），必须继承持久身份。
        clonedConstraint.persistentId = sourceConstraint.persistentId;
        clonedConstraint.setSuppressed(sourceConstraint.isSuppressed);
        clonedConstraint.setBranchHint(sourceConstraint.getBranchHint());
        return clonedConstraint;
    } catch (error) {
        throw new Error(
            `约束复制失败：${sourceConstraint.baseDisplayName} 不支持无参复制构造，且未实现 cloneSelf()。原始错误：${String(error)}`,
        );
    }
}

/**
 * 为对象列表上的全部几何关系重新生成持久 ID。
 * 说明：
 * 1. 供“复制出新逻辑实体”的流程使用（设计树复制草图、复制实体等）；
 * 2. 多目标关系实例被多个对象共享，通过访问集合保证每条关系只重新生成一次；
 * 3. 只改身份，不触碰关系类型、目标绑定与求解状态。
 * @param objects 目标对象列表。
 */
export function regenerateConstraintPersistentIds(objects: readonly ISketchObject[]): void {
    const visitedConstraintSet = new Set<SketchRelationDefinition<ISketchObject>>();
    objects.forEach((object) => {
        object.constraints.getRelations().forEach((constraint) => {
            const normalizedConstraint = constraint as SketchRelationDefinition<ISketchObject>;
            if (visitedConstraintSet.has(normalizedConstraint)) {
                return;
            }
            visitedConstraintSet.add(normalizedConstraint);
            normalizedConstraint.persistentId = createSketchConstraintPersistentId();
        });
    });
}

